import { findStandard } from './calculations';
import { normCpk } from './cpk';

// Canonical element shape (v2.3, Task #27 adds optional `material`) +
// helpers that bridge math/exports/UI without each consumer needing
// to know whether tolerances are stored symmetrically or asymmetrically.
//
// Shape:
//   {
//     id:           string,
//     name:         string,                             // was `label`
//     nominal:      number,
//     direction:    1 | -1,
//     tolMode:      'symmetric' | 'asymmetric',
//     tol:          number,                             // one-sided ± when symmetric
//     tolUpper:     number,                             // signed deviation when asymmetric
//     tolLower:     number,                             // signed deviation when asymmetric
//     distribution: 'normal' | 'uniform' | 'triangular',
//     norm:         string,                             // '' = manual; was `source`
//     material:     string,                             // optional; '' = kein thermischer Anteil
//   }
//
// v2.3 erweitert v2.2 abwärtskompatibel um `material`. Elemente ohne
// material verhalten sich identisch zu früher (kein thermischer Beitrag).
// Genauso laufen alte v2.2-Saves (Cloud + .tcp) unverändert weiter.
//
// Hard cut von v2.1 → v2.2 — global `currentStandard` ist gone und
// `lower`/`upper` werden abgeleitet (nie gespeichert). Per design
// decision in memory `schema-per-row-norm`, kein Migrations-Pfad für
// alte .tcp-Files; vor v2.2 gab es keine in the wild (Pre-Launch).

const DISTRIBUTIONS = ['normal', 'uniform', 'triangular'];
const DIRECTIONS = [1, -1];
const TOL_MODES = ['symmetric', 'asymmetric'];

// Random id helper. crypto.randomUUID() exists in modern browsers +
// Node 18+; sliced to keep ids short for diff readability.
function generateId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID().slice(0, 9);
  }
  return Math.random().toString(36).slice(2, 11);
}

/**
 * Resolves a stored element to the signed (lower, upper) deviations
 * the math layer needs. Symmetric → ±tol, asymmetric → as stored.
 * Anywhere computation reads tolerances it goes through here.
 */
export function getDeviations(el) {
  if (el?.tolMode === 'asymmetric') {
    return {
      lower: Number(el.tolLower) || 0,
      upper: Number(el.tolUpper) || 0,
    };
  }
  const tol = Math.abs(Number(el?.tol) || 0);
  return { lower: -tol, upper: tol };
}

/**
 * Inverse of getDeviations: takes a raw (lower, upper) pair (e.g.
 * the output of a norm formula like iso2768m) and picks the most
 * faithful storage mode. A pair within ~1e-9 of being mirrored is
 * stored as symmetric; everything else as asymmetric so signed
 * deviations round-trip exactly.
 */
export function fromDeviations(lower, upper) {
  const lo = Number(lower) || 0;
  const up = Number(upper) || 0;
  if (Math.abs(lo + up) < 1e-9) {
    return { tolMode: 'symmetric', tol: Math.abs(up), tolUpper: 0, tolLower: 0 };
  }
  return { tolMode: 'asymmetric', tol: 0, tolUpper: up, tolLower: lo };
}

/**
 * Picks a sensible Monte-Carlo distribution default for a given norm
 * id. Plastic-injection (DIN 16742) skews triangular by industry
 * convention; everything else defaults to normal. Manual (norm='')
 * also defaults to normal — the user can override per-row.
 */
export function suggestDistribution(norm) {
  if (!norm) return 'normal';
  if (norm.startsWith('din16742')) return 'triangular';
  return 'normal';
}

/**
 * Builds a canonical element from a partial input. Used by:
 *   - store.addElement (UI form submit)
 *   - store.addIsoElement (quick-add via norm)
 *   - tcpFormat.parseTcp (file import — though it already validates
 *     the same shape via zod)
 *   - templates / fixtures
 *
 * Accepts either `tolMode`+`tol`/`tolUpper`/`tolLower` directly OR a
 * convenience `lower`/`upper` pair which gets converted via
 * fromDeviations. This is the only sanctioned source of element ids,
 * so two elements never collide.
 */
export function makeElement(input = {}) {
  const direction = DIRECTIONS.includes(Number(input.direction)) ? Number(input.direction) : 1;
  const distribution = DISTRIBUTIONS.includes(input.distribution)
    ? input.distribution
    : suggestDistribution(input.norm || '');

  let tolMode;
  let tol;
  let tolUpper;
  let tolLower;

  if (TOL_MODES.includes(input.tolMode)) {
    tolMode = input.tolMode;
    tol = Math.abs(Number(input.tol) || 0);
    tolUpper = Number(input.tolUpper) || 0;
    tolLower = Number(input.tolLower) || 0;
  } else if (input.lower !== undefined || input.upper !== undefined) {
    // Convenience: caller gave us raw deviations (e.g. from a norm
    // formula like iso2768m). Pick the storage mode automatically.
    const derived = fromDeviations(input.lower, input.upper);
    tolMode = derived.tolMode;
    tol = derived.tol;
    tolUpper = derived.tolUpper;
    tolLower = derived.tolLower;
  } else {
    tolMode = 'symmetric';
    tol = 0;
    tolUpper = 0;
    tolLower = 0;
  }

  const cpk = normCpk(input.cpk);

  return {
    id: input.id || generateId(),
    // An explicit empty string is kept as-is so the row's Bezeichnung
    // input shows its "z.B. Gehäuselänge" placeholder. The previous
    // fallback ('M') stamped every new row with literal "M" text,
    // which buried the placeholder and forced users to clear it first.
    name: typeof input.name === 'string'
      ? input.name
      : (typeof input.label === 'string' ? input.label : ''),
    nominal: Number(input.nominal) || 0,
    direction,
    // C5: deaktiviertes Maß (nicht in der Berechnung, Richtung bleibt).
    inactive: input.inactive === true,
    tolMode,
    tol,
    tolUpper,
    tolLower,
    distribution,
    // C+17: Cpk je Maß, nur wenn gültig gesetzt. Fehlt der Schlüssel,
    // rechnet Monte Carlo mit 1,0. Bleibt bei Gleich/Dreieck erhalten,
    // wirkt dort aber nicht.
    ...(cpk !== null ? { cpk } : {}),
    norm: typeof input.norm === 'string' ? input.norm : '',
    // material: optional, leer = kein thermischer Beitrag in der
    // Berechnung (Verhalten = wie vor Task #27). Wir normalisieren
    // undefined/null/non-string zu '' damit gespeicherte Projekte
    // und der ===''-Check in der UI sich konsistent verhalten.
    material: typeof input.material === 'string' ? input.material : '',
  };
}

/**
 * Computes the (lower, upper) deviations the row's norm WOULD produce
 * at the row's current nominal. Returns null when the row has no norm
 * or the norm has no formula (e.g. some informational standards).
 * Centralises the lookup so the override-detection + clear-to-norm
 * code in updateElement + the MeasureTable "overridden" UI cue all
 * agree on a single source of truth.
 */
/**
 * True, wenn die Zeile eine Norm trägt, deren Tabelle das Nennmaß nicht
 * abdeckt (z. B. ISO 286-2 über 250 mm). Die Norm liefert dann 0/0 statt
 * erfundener Werte; die NormCell macht das als Warnung sichtbar.
 */
export function isNormOutOfRange(el) {
  if (!el?.norm) return false;
  const standard = findStandard(el.norm);
  if (!standard?.getTol) return false;
  try {
    return standard.getTol(el.nominal)?.outOfRange === true;
  } catch {
    return false;
  }
}

export function getNormDeviations(el) {
  if (!el?.norm) return null;
  const standard = findStandard(el.norm);
  if (!standard?.getTol) return null;
  try {
    const { lower, upper } = standard.getTol(el.nominal);
    if (!Number.isFinite(lower) || !Number.isFinite(upper)) return null;
    return { lower, upper };
  } catch {
    return null;
  }
}

/**
 * True when the row carries a norm but the stored tolerance values
 * have been manually edited away from what the norm would produce.
 * Comparison uses 1e-9 to absorb FP noise from sym ↔ asym round-trips.
 *
 * Drives:
 *   - the dashed/grey "Norm vorgemerkt — überschrieben" cue in the
 *     MeasureTable's NormCell
 *   - the export gate (getEffectiveNorm): exports must show the
 *     manual value as manual, not as a norm citation
 *   - the "clear back to zero restores the norm" logic in
 *     toleranceStore.updateElement
 */
export function isOverriddenFromNorm(el) {
  const fromNorm = getNormDeviations(el);
  if (!fromNorm) return false;
  const stored = getDeviations(el);
  return Math.abs(stored.lower - fromNorm.lower) > 1e-9
      || Math.abs(stored.upper - fromNorm.upper) > 1e-9;
}

/**
 * Norm string to use in exports: the row's norm if the value still
 * matches the norm formula, '' if the user has manually overridden
 * it. A manually-overridden row exports as a manual entry — citing
 * the norm would lie about the source of the number.
 */
export function getEffectiveNorm(el) {
  if (!el?.norm) return '';
  return isOverriddenFromNorm(el) ? '' : el.norm;
}

/**
 * Nur die Maße, die in die Berechnung eingehen (C5). Gleiche Objekte,
 * gleiche Reihenfolge.
 */
export function activeElements(elements) {
  return (elements || []).filter((el) => el?.inactive !== true);
}

/**
 * Aktive Maße mit ihrer Tabellennummer `nr` (1-basiert). Für Anzeigen,
 * die „M2“ auch dann „M2“ nennen müssen, wenn M1 inaktiv ist.
 */
export function activeWithNumbers(elements) {
  return (elements || [])
    .map((el, i) => ({ ...el, nr: i + 1 }))
    .filter((el) => el.inactive !== true);
}
